import { useAppConfig } from '../../features/acl/acl-provider.tsx';
import { toCurrencyString, toCurrencyStringOrEmpty } from '../currency-helpers.ts';
import { useLocale } from './use-locale.ts';

type FormatCurrencyOptions = {
    decimals?: number;
    currency?: string;
    showEmptyIfZero?: boolean;
    emptyText?: string;
    showPlusSignIfPositive?: boolean;
};

export function useCurrencyHelpers() {
    const { locale } = useLocale();
    const { defaultCurrency } = useAppConfig();

    const formatCurrency = (
        value: string | number | undefined | null,
        options: FormatCurrencyOptions = {},
    ): string => {
        const {
            decimals = 0,
            currency,
            showEmptyIfZero = false,
            emptyText = '-',
            showPlusSignIfPositive = false,
        } = options;

        const cleanValue = Number(value);

        if (Number.isNaN(cleanValue) || !Number.isFinite(cleanValue)) {
            return emptyText;
        }

        if (cleanValue === 0 && showEmptyIfZero) {
            return emptyText;
        }

        if (showPlusSignIfPositive) {
            const currencyToUse = currency || defaultCurrency;
            const safeCurrency = currencyToUse && currencyToUse.length === 3 ? currencyToUse : 'EUR';
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: safeCurrency,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
                signDisplay: 'always',
            }).format(cleanValue);
        }

        return toCurrencyString(cleanValue, locale, decimals, currency || defaultCurrency);
    };

    return {
        toCurrencyString: (value: number, decimals = 0, customCurrency?: string) =>
            toCurrencyString(value, locale, decimals, customCurrency || defaultCurrency),
        toCurrencyStringOrEmpty: (
            value?: number | null,
            decimals = 2,
            customCurrency?: string,
            defaultEmpty = '-',
        ) =>
            toCurrencyStringOrEmpty(value, locale, decimals, customCurrency || defaultCurrency, defaultEmpty),
        formatCurrency,
    };
}
